/**
 * Install @page + composed-page break CSS for any print-engine document.
 *
 * Also sizes `#print-container` for the active page — global letter CSS
 * (`PrintableQuotation.css` etc.) forces `width: 8.5in`, which letterboxes
 * A4 landscape report pages and leaves a large blank gap above the title.
 */
const STYLE_ID = 'print-engine-page-css';

const COMPOSED_ROOT =
  '.print-paginated-root, .qtn-paginated-root, .printable-quotation.quotation-qtn-format';

export function installPrintPageCss(opts?: { pageSize?: 'letter' | 'a4' | 'a4 landscape' }): () => void {
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    el.setAttribute('data-print-engine', '1');
  }
  const size = opts?.pageSize ?? 'letter';
  const containerWidth =
    size === 'a4 landscape' ? '297mm' : size === 'a4' ? '210mm' : '8.5in';
  el.textContent = `
@media print {
  @page {
    size: ${size} !important;
    margin: 0 !important;
  }
  /* Beat global letter #print-container { width: 8.5in } for landscape reports. */
  #print-container {
    width: ${containerWidth} !important;
    max-width: ${containerWidth} !important;
    margin: 0 auto !important;
    padding: 0 !important;
  }
  ${COMPOSED_ROOT} {
    height: auto !important;
    min-height: 0 !important;
    page-break-after: auto !important;
  }
  ${COMPOSED_ROOT} .print-composed-page,
  ${COMPOSED_ROOT} .qtn-composed-page {
    page-break-after: always !important;
    page-break-inside: avoid !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  ${COMPOSED_ROOT} .print-composed-page:last-child,
  ${COMPOSED_ROOT} .qtn-composed-page:last-child {
    page-break-after: auto !important;
  }
}
`;
  document.head.appendChild(el);
  return () => {
    document.getElementById(STYLE_ID)?.remove();
  };
}

/** @deprecated Use installPrintPageCss */
export function installSalesQuotationPrintPageMargins(): () => void {
  return installPrintPageCss({ pageSize: 'letter' });
}

export function isSalesQuotationPrintRoot(root: ParentNode | null | undefined): boolean {
  if (!root) return false;
  return !!root.querySelector(
    '.printable-quotation.quotation-qtn-format:not(.printable-delivery-note):not(.printable-packing-list):not(.printable-purchase-order):not(.printable-sar), .print-paginated-root'
  );
}
